import React, { useEffect, useRef, useState } from 'react'
import { User } from 'oidc-client';
import { AgTechAuthContext, AuthContext, useAgTechAuthContext } from '@agtech/core/auth/AgTechAuthContext';
import AgTechWebAuthenticationService from './AgTechWebAuthenticationService';
import { AgTechApplication } from '@agtech/core/app/AgTechApplication';

declare type AuthContextState = {
    user: User | null,
    hasLoaded: boolean
}

const DefaultUser = {
    id: '',
    name: 'Mock User',
    username: 'mockusername',
    email: 'mockuser@gmail.com',
    phoneNumber: '111-123-2932',
    token: '',
    role: ['user']
};

const AgTechWebAppAuthContext = (props: {
    app: AgTechApplication,
    children: JSX.Element
}) => {
    const appAuthConfiguration = props.app.auth;
    const authService = useRef(new AgTechWebAuthenticationService(appAuthConfiguration));

    const [authState, updateAuthState] = useState<AuthContextState>({
        user: null,
        hasLoaded: false
    });

    const doesAppRequireAuthentication = appAuthConfiguration !== undefined && appAuthConfiguration.usesAuth;

    useEffect(() => {
        if (doesAppRequireAuthentication) {
            authService.current.getUser()
                .then(user => {                   
                    updateAuthState({
                        user: user,
                        hasLoaded: true
                    });
                });
        }
        else {
            updateAuthState({
                user: null,
                hasLoaded: true
            });
        }
    }, []);

    let authContext: AgTechAuthContext = {
        getLoggedInUser: async () => {
            if (doesAppRequireAuthentication) {
                let loggedInUser = await authService.current.getUser();
                let role = [];

                if (loggedInUser?.profile?.role) {
                    // If there are multiple roles specified it will be an array from the server, otherwise a single value
                    if (typeof loggedInUser?.profile?.role === 'string') {
                        role = [loggedInUser?.profile?.role];
                    }
                    else {
                        role = loggedInUser?.profile?.role;
                    }
                }

                return {
                    id: loggedInUser?.profile?.sub ?? '',
                    name: loggedInUser?.profile?.name ?? '',
                    username: loggedInUser?.profile?.preferred_username ?? '',
                    token: loggedInUser?.access_token ?? '',
                    role: role
                }
            }
            else {
                return DefaultUser;
            }
        },
        isUserAuthenticated: () => {
            return !doesAppRequireAuthentication || (authState.user?.access_token !== undefined);
        },
        loginUser: () => authService.current.login(),
        onSuccessfulUserLogin: async props => {
            await authService.current.signinRedirectCallback(props)
                .then(loggedInUser => {                    
                    updateAuthState({
                        user: loggedInUser,
                        hasLoaded: true
                    });

                    props.navigateToRoot();
                })
                .catch(error => {
                    console.error(error);
                    return null;
                });
        },
        logoutUser: () => authService.current.signoutRedirectCallback(),
        user: doesAppRequireAuthentication ? {
            id: authState.user?.profile?.sub ?? '',
            username: authState.user?.profile?.preferred_username ?? '',
            name: authState.user?.profile?.name ?? '',
            token: authState.user?.access_token ?? '',
            role: typeof authState.user?.profile?.role === 'string' ? [authState.user?.profile?.role] : authState.user?.profile?.role
        } : DefaultUser,
        config: appAuthConfiguration
    };

    return authState.hasLoaded ? (
        <AuthContext.Provider value={authContext}>
            {props.children}
        </AuthContext.Provider>
    ) : null
};

export const useUserRoles = () => useAgTechAuthContext().user.role;

export default React.memo(AgTechWebAppAuthContext);